import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { getBlogPosts, getBlogPost } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Calendar, Eye, User, ArrowLeft, Loader2 } from 'lucide-react';
import AdBanner from '../components/AdBanner';

const BlogPage = () => {
  const { language } = useLanguage();
  const { postId } = useParams();

  if (postId) {
    return <BlogPostView postId={postId} />;
  }

  return <BlogListView />;
};

const BlogListView = () => {
  const { language } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      const res = await getBlogPosts();
      setPosts(res.data);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-16 px-4 md:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-3xl md:text-4xl font-bold mb-4" data-testid="blog-title">
            Blog Roomia.net
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {language === 'fr'
              ? 'Conseils, actualités et informations sur le logement au Cameroun et en Afrique.'
              : 'Tips, news and information about housing in Cameroon and Africa.'}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Posts List */}
          <div className="lg:col-span-2 space-y-6">
            {posts.length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center text-muted-foreground">
                  {language === 'fr' ? 'Aucun article pour le moment.' : 'No articles yet.'}
                </CardContent>
              </Card>
            ) : (
              posts.map((post) => (
                <Link key={post.post_id} to={`/blog/${post.post_id}`} data-testid={`blog-card-${post.post_id}`}>
                  <Card className="overflow-hidden hover:shadow-lg transition-all hover:-translate-y-1">
                    <div className="md:flex">
                      {post.cover_image && (
                        <div className="md:w-1/3 aspect-video md:aspect-auto">
                          <img
                            src={post.cover_image}
                            alt={post.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <CardContent className={`p-6 ${post.cover_image ? 'md:w-2/3' : 'w-full'}`}>
                        <div className="flex flex-wrap gap-2 mb-3">
                          {post.tags?.slice(0, 3).map((tag) => (
                            <Badge key={tag} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                        <h2 className="font-heading text-xl font-semibold mb-2 line-clamp-2 hover:text-primary transition-colors">
                          {post.title}
                        </h2>
                        {post.excerpt && (
                          <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                            {post.excerpt}
                          </p>
                        )}
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <User className="h-4 w-4" />
                            {post.author_name || 'Admin'}
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {new Date(post.created_at).toLocaleDateString()}
                          </div>
                          <div className="flex items-center gap-1">
                            <Eye className="h-4 w-4" />
                            {post.views}
                          </div>
                        </div>
                      </CardContent>
                    </div>
                  </Card>
                </Link>
              ))
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-6">
            <AdBanner position="sidebar" />
            
            <Card>
              <CardContent className="p-6">
                <h3 className="font-heading font-semibold mb-4">
                  {language === 'fr' ? 'À propos du blog' : 'About the blog'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {language === 'fr'
                    ? 'Retrouvez ici nos conseils pour trouver le logement idéal, les actualités du marché immobilier camerounais et des informations pratiques pour votre séjour.'
                    : 'Find here our tips for finding the ideal accommodation, news from the Cameroonian real estate market and practical information for your stay.'}
                </p>
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>
    </div>
  );
};

const BlogPostView = ({ postId }) => {
  const { language } = useLanguage();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPost();
  }, [postId]);

  const loadPost = async () => {
    try {
      const res = await getBlogPost(postId);
      setPost(res.data);
    } catch (error) {
      console.error('Error loading post:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-muted-foreground mb-4">
          {language === 'fr' ? 'Article non trouvé' : 'Article not found'}
        </p>
        <Link to="/blog">
          <Button variant="outline" className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            {language === 'fr' ? 'Retour au blog' : 'Back to blog'}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 md:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Link to="/blog" className="inline-block mb-6">
          <Button variant="ghost" className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            {language === 'fr' ? 'Retour au blog' : 'Back to blog'}
          </Button>
        </Link>

        {/* Cover Image */}
        {post.cover_image && (
          <div className="aspect-video rounded-2xl overflow-hidden mb-8">
            <img
              src={post.cover_image}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {post.tags?.map((tag) => (
            <Badge key={tag} variant="secondary">
              {tag}
            </Badge>
          ))}
        </div>

        {/* Title */}
        <h1 className="font-heading text-3xl md:text-4xl font-bold mb-4" data-testid="blog-post-title">
          {post.title}
        </h1>

        {/* Meta */}
        <div className="flex items-center gap-4 text-muted-foreground mb-8">
          <div className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {post.author_name || 'Admin'}
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {new Date(post.created_at).toLocaleDateString()}
          </div>
          <div className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            {post.views} {language === 'fr' ? 'vues' : 'views'}
          </div>
        </div>

        <Separator className="mb-8" />

        {/* Content */}
        <article className="prose prose-lg max-w-none dark:prose-invert" data-testid="blog-post-content">
          {post.content.split('\n').map((paragraph, index) => {
            if (paragraph.startsWith('# ')) {
              return <h1 key={index} className="font-heading">{paragraph.slice(2)}</h1>;
            } else if (paragraph.startsWith('## ')) {
              return <h2 key={index} className="font-heading">{paragraph.slice(3)}</h2>;
            } else if (paragraph.startsWith('### ')) {
              return <h3 key={index} className="font-heading">{paragraph.slice(4)}</h3>;
            } else if (paragraph.startsWith('- ')) {
              return <li key={index}>{paragraph.slice(2)}</li>;
            } else if (paragraph.trim()) {
              return <p key={index}>{paragraph}</p>;
            }
            return null;
          })}
        </article>

        {/* Ad */}
        <div className="mt-12">
          <AdBanner position="inline" />
        </div>
      </div>
    </div>
  );
};

export default BlogPage;
